/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React from 'react';
import { Typography, Box, Grid } from '@mui/material';
import modifyScriptName from '../../../../utils/modifyScriptName';
import { fDateTime } from '../../../../utils/formatTime';

const TradeHistoryRow = ({ trade, isDealsTab = false }) => {
  const { 
    type, 
    buy, 
    sell, 
    buyBrokerage, 
    sellBrokerage, 
    boughtAt, 
    soldAt, 
    script, 
    profit, 
    brokerage, 
    side, 
    updatedAt,
    createdAt,
    buyQuantity,
    sellQuantity,
    closedQuantity
  } = trade;
  let color;
  if (['EXECUTED', 'CANCELLED'].includes(type)) {
    color = 'green';
  }
  if (['OPEN', 'ORDER', 'CLOSE'].includes(type)) {
    if (side === 'BUY') {
      color = 'blue';
    } else {
      color = 'red';
    }
  }
  
  // Get quantity display
  const getQuantity = () => {
    // For CLOSE type trades - show closedQuantity/totalQuantity if closedQuantity is present
    if (type === 'CLOSE') {
      const totalQuantity = side === 'BUY' ? buyQuantity : sellQuantity;
      if (closedQuantity !== undefined && closedQuantity !== null && 
          totalQuantity !== undefined && totalQuantity !== null) {
        return `${closedQuantity}/${totalQuantity}`;
      }
      return totalQuantity !== undefined && totalQuantity !== null ? totalQuantity : trade.quantity;
    }
    
    // For OPEN type trades - show closedQuantity/totalQuantity if closedQuantity is present
    if (type === 'OPEN') {
      const actualQuantity = side === 'BUY' 
        ? (buyQuantity !== undefined && buyQuantity !== null ? buyQuantity : trade.quantity)
        : (sellQuantity !== undefined && sellQuantity !== null ? sellQuantity : trade.quantity);
      
      // If closedQuantity exists, show closedQuantity/totalQuantity
      if (closedQuantity !== undefined && closedQuantity !== null && 
          actualQuantity !== undefined && actualQuantity !== null) {
        return `${closedQuantity}/${actualQuantity}`;
      }
      
      // Otherwise, return the actual quantity
      return actualQuantity;
    }
    
    // For ORDER and other types, just return the regular quantity
    return trade.quantity;
  };
  
  const quantity = getQuantity();
  return (
    <Box sx={{ borderBottom: '0.5px solid grey', width: '100%', py: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {/* First column */}
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">{modifyScriptName(script.tradingsymbol)},</Typography>
          <Typography
            variant="h5"
            color={color}
          >
            {side} {quantity}
          </Typography>
        </Box>
        {/* Date and Profit section - unified for all types */}
        {type === 'ORDER' ? (
          // For ORDER type, show both boughtAt and soldAt in separate rows
          <Box>
            {/* First row: boughtAt */}
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid container columnGap={2.5}>
                <Grid item>
                  <Typography variant="body2">boughtAt: </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2">
                    {boughtAt ? fDateTime(boughtAt) : createdAt ? fDateTime(createdAt) : '--'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            {/* Second row: soldAt and profit/loss */}
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid container columnGap={2.5}>
                <Grid item>
                  <Typography variant="body2">soldAt: </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2">
                    {soldAt ? fDateTime(soldAt) : '--'}
                  </Typography>
                </Grid>
              </Grid>
              {/* Show profit only if profit field exists (and not 0) OR closedQuantity exists */}
              {(((profit !== undefined && profit !== null && profit !== 0)) || (closedQuantity !== undefined && closedQuantity !== null)) && profit !== undefined && profit !== null ? (
                <Typography variant="h4" color={`${profit > 0 ? 'blue' : 'red'}`}>
                  {profit.toFixed(2)}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ) : (
          // For other types, show based on side
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid container columnGap={2.5}>
              <Grid item>
                <Typography variant="body2">
                  {side === 'BUY' ? 'boughtAt: ' : 'soldAt: '}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2">
                  {side === 'BUY'
                    ? (boughtAt ? fDateTime(boughtAt) : createdAt ? fDateTime(createdAt) : '--')
                    : (soldAt ? fDateTime(soldAt) : createdAt ? fDateTime(createdAt) : '--')
                  }
                </Typography>
              </Grid>
            </Grid>
            {/* Show profit only if profit field exists (and not 0) OR closedQuantity exists */}
            {(((profit !== undefined && profit !== null && profit !== 0)) || (closedQuantity !== undefined && closedQuantity !== null)) && profit !== undefined && profit !== null ? (
              <Typography variant="h4" color={`${profit > 0 ? 'blue' : 'red'}`}>
                {profit.toFixed(2)}
              </Typography>
            ) : null}
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {/* Buy and sell prices */}
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              {/* Buy price - show for BUY side trades (all types except CLOSE SELL) */}
              {side === 'BUY' && (type !== 'CLOSE' || buy) && (
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Buy:</Typography>
                  <Typography variant="body2"> {buy ? buy.toFixed(2) : '--'}</Typography>
                </Box>
              )}
              {/* Sell price - show for SELL side trades or CLOSE trades with sell */}
              {((side === 'SELL' && type !== 'CLOSE') || (type === 'CLOSE' && sell) || (type === 'ORDER' && sell)) && (
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sell:</Typography>
                  <Typography variant="body2"> {sell ? sell.toFixed(2) : '--'}</Typography>
                </Box>
              )}
              {/* Show buy for ORDER type when both buy and sell exist */}
              {type === 'ORDER' && buy && (
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Buy:</Typography>
                  <Typography variant="body2"> {buy.toFixed(2)}</Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            {type === 'CLOSE' && (
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Typography variant="body2">Brokerage:</Typography>
                <Typography variant="body2"> {brokerage?.toFixed(2) || '--'}</Typography>
              </Box>
            )}
            {type === 'OPEN' && (
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Typography variant="body2">Brokerage:</Typography>
                <Typography variant="body2"> {brokerage?.toFixed(2) || '--'}</Typography>
              </Box>
            )}
            {type === 'ORDER' && (
              <>
                {' '}
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Buy Brokerage:</Typography>
                  <Typography variant="body2"> {buyBrokerage?.toFixed(2) || '--'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sell Brokerage:</Typography>
                  <Typography variant="body2"> {sellBrokerage?.toFixed(2) || '--'}</Typography>
                </Box>
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TradeHistoryRow;
